import { useMemo } from "react";
import { Link } from "react-router-dom";
import { films, filmTypes } from "@/mocks/films";
import { useFavorites } from "@/hooks/useFavorites";
import HeroCarousel from "./components/HeroCarousel";
import CategoryEntries from "./components/CategoryEntries";
import FilmGrid from "@/components/feature/FilmGrid";
import SectionHeading from "@/components/feature/SectionHeading";

const categoryMeta: Record<string, { icon: string; desc: string }> = {
  movie: { icon: "ri-film-line", desc: "深度剧情长片，聚焦人性与社会议题" },
  tv: { icon: "ri-live-line", desc: "连续剧集，细腻刻画亲密关系与家庭" },
  short: { icon: "ri-clapperboard-line", desc: "精炼短片，以短小篇幅直击主题" },
};

export default function Home() {
  const { favorites, toggleFavorite } = useFavorites();

  const featured = useMemo(
    () => [...films].sort((a, b) => b.popularity - a.popularity).slice(0, 5),
    [],
  );
  const latest = useMemo(
    () =>
      [...films]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 8),
    [],
  );
  const topRated = useMemo(
    () => [...films].sort((a, b) => b.rating - a.rating).slice(0, 4),
    [],
  );
  const popularTv = useMemo(
    () => [...films].filter((f) => f.type === "tv").sort((a, b) => b.popularity - a.popularity).slice(0, 4),
    [],
  );

  const entries = filmTypes.map((t) => ({
    type: t.key,
    label: t.label,
    icon: categoryMeta[t.key].icon,
    desc: categoryMeta[t.key].desc,
    count: films.filter((f) => f.type === t.key).length,
  }));

  return (
    <div>
      <HeroCarousel films={featured} favorites={favorites} onToggleFavorite={toggleFavorite} />

      <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-14">
        <SectionHeading
          icon="ri-grid-line"
          title="影视线分类"
          subtitle="按作品形态快速找到你想看的伦理影视内容"
        />
        <CategoryEntries entries={entries} />
      </div>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-14">
          <SectionHeading
            icon="ri-history-line"
            title="最新上线"
            subtitle="近期更新与上线的伦理影视作品"
            moreTo="/category/movie"
          />
          <FilmGrid films={latest} favorites={favorites} onToggleFavorite={toggleFavorite} />
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-14">
        <SectionHeading
          icon="ri-award-line"
          title="口碑佳作"
          subtitle="评分居前的伦理影片，值得细细品味"
          moreTo="/category/movie"
        />
        <FilmGrid
          films={topRated}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          columns={4}
        />
      </div>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-14">
          <SectionHeading
            icon="ri-tv-2-line"
            title="热门伦理剧集"
            subtitle="持续更新，陪你度过每个夜晚"
            moreTo="/category/tv"
          />
          <FilmGrid
            films={popularTv}
            favorites={favorites}
            onToggleFavorite={toggleFavorite}
            columns={4}
          />
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-14">
        <div className="overflow-hidden rounded-lg bg-primary-800">
          <div className="flex flex-col gap-6 p-8 md:flex-row md:items-center md:justify-between md:p-10">
            <div>
              <h2 className="font-heading text-2xl font-bold text-background-50">
                关于伦理片在线观看
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-primary-100">
                我们聚合伦理类电影、电视剧与短片的作品资料，帮助观众更高效地发现优质内容。所有作品信息均可查阅、可复制，方便收藏与分享。
              </p>
            </div>
            <Link
              to="/about"
              className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              了解更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}